import { Box, Container, Typography, Button, Link, Stack } from "@mui/material";
import { Icon } from "@iconify/react";
import { Helmet } from "react-helmet-async";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Footer from "../components/Footer";
import { findNews, SITE_URL } from "../content/news";

function ListItemText({ item }) {
    return (
        <>
            {item.lead && <Box component="strong">{item.lead} </Box>}
            {item.text}
        </>
    );
}

function Block({ block, onNavigate }) {
    switch (block.type) {
        case "h2":
            return (
                <Typography variant="h5" component="h2" sx={{ fontWeight: 800, mt: 4, mb: 1.5 }}>
                    {block.text}
                </Typography>
            );
        case "h3":
            return (
                <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
                    {block.text}
                </Typography>
            );
        case "ul":
        case "ol":
            return (
                <Box component={block.type} sx={{ pl: 3, mb: 2, "& li": { mb: 1, lineHeight: 1.7 } }}>
                    {block.items.map((item, i) => (
                        <Typography component="li" variant="body1" key={i}>
                            <ListItemText item={item} />
                        </Typography>
                    ))}
                </Box>
            );
        case "cta":
            return (
                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        size="large"
                        href={block.to}
                        onClick={(e) => {
                            e.preventDefault();
                            onNavigate(block.to);
                        }}
                        startIcon={<Icon icon="mdi:magnify" />}
                        sx={{ borderRadius: 2, px: 4 }}
                    >
                        {block.text}
                    </Button>
                </Box>
            );
        default:
            return (
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                    {block.text}
                </Typography>
            );
    }
}

export default function NewsArticlePage() {
    const { slug } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const article = findNews(slug);

    if (!article) {
        return <Navigate to="/novosti" replace />;
    }

    const url = `${SITE_URL}/novosti/${article.slug}`;
    const imageUrl = `${SITE_URL}${article.image}`;
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.seoDescription,
        image: [imageUrl],
        datePublished: article.date,
        dateModified: article.updated || article.date,
        mainEntityOfPage: url,
        author: { "@type": "Organization", name: "BusGo", url: SITE_URL },
        publisher: {
            "@type": "Organization",
            name: "BusGo",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/assets/logo/appLogo.png` },
        },
    };

    return (
        <Box>
            <Helmet>
                <title>{article.seoTitle}</title>
                <meta name="description" content={article.seoDescription} />
                <meta property="og:title" content={article.seoTitle} />
                <meta property="og:description" content={article.seoDescription} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={url} />
                <meta property="og:image" content={imageUrl} />
                <link rel="canonical" href={url} />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>

            <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "grey.100", py: { xs: 3, md: 5 } }}>
                <Container maxWidth="md">
                    <Link
                        href="/novosti"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate("/novosti");
                        }}
                        underline="hover"
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 2, fontWeight: 600 }}
                    >
                        <Icon icon="mdi:arrow-left" width={18} />
                        {t("news.back")}
                    </Link>

                    <Box component="article" sx={{ bgcolor: "white", borderRadius: 3, overflow: "hidden" }}>
                        <Box
                            component="img"
                            src={article.image}
                            alt={article.imageAlt}
                            sx={{ display: "block", width: "100%", aspectRatio: "3 / 2", objectFit: "cover" }}
                        />
                        <Box sx={{ p: { xs: 3, md: 5 } }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, color: "text.secondary" }}>
                                <Icon icon="mdi:calendar-blank-outline" width={16} />
                                <Typography variant="body2" component="time" dateTime={article.date}>
                                    {dayjs(article.date).format("DD.MM.YYYY.")}
                                </Typography>
                            </Stack>
                            <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 3, fontSize: { xs: "1.75rem", md: "2.5rem" } }}>
                                {article.title}
                            </Typography>
                            {article.blocks.map((block, i) => (
                                <Block key={i} block={block} onNavigate={navigate} />
                            ))}
                        </Box>
                    </Box>
                </Container>
            </Box>

            <Footer />
        </Box>
    );
}
